import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function ServicesManager() {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", price: "", unit: "each" });

  const load = useCallback(() => http.get("/admin/services").then((r) => setServices(r.data.services)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || form.price === "") return toast.error("Name and price are required");
    try {
      await http.post("/admin/services", { ...form, price: parseFloat(form.price) });
      toast.success("Service added");
      setForm({ name: "", description: "", price: "", unit: "each" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const save = async (s, patch) => {
    try {
      await http.put(`/admin/services/${s.id}`, {
        name: s.name, description: s.description || "", price: s.price, unit: s.unit || "each",
        tax_rate: s.tax_rate ?? 0, available: s.available ?? true, active: s.active ?? true,
        sort_order: s.sort_order ?? 999, image: s.image || "", ...patch,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const archive = async (id) => {
    try { await http.delete(`/admin/services/${id}`); toast.success("Service archived"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="services-manager">
      <div className="card">
        <h3 className="font-serif text-2xl mb-4">Add Service</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <input data-testid="svc-form-name" placeholder="Service name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="svc-form-price" type="number" min="0" placeholder="Price (₹)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <input data-testid="svc-form-unit" placeholder="Unit (each/night/session)" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          <input data-testid="svc-form-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button data-testid="svc-form-add" onClick={add} className="btn-primary flex items-center justify-center gap-2 md:col-span-4"><Plus size={16} /> Add Service</button>
        </div>
      </div>

      <div className="card">
        <h3 className="font-serif text-2xl mb-3">Services & Pricing</h3>
        <div className="space-y-2">
          {services.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-2 py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`svc-row-${s.id}`}>
              <span className="font-medium flex-1 min-w-32">
                {s.name}
                {!s.active && <span className="badge-terracotta ml-2">archived</span>}
                {s.active && !s.available && <span className="badge-terracotta ml-2">unavailable</span>}
                <span className="text-xs ml-2" style={{ color: "var(--muted)" }}>/ {s.unit}</span>
              </span>
              <input data-testid={`svc-price-${s.id}`} type="number" min="0" defaultValue={s.price} key={`sp-${s.id}-${s.price}`}
                onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== s.price) save(s, { price: v }); }}
                style={{ width: 90, padding: "6px 10px" }} />
              <button data-testid={`svc-toggle-${s.id}`} onClick={() => save(s, { available: !s.available })} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>
                {s.available ? "Mark unavailable" : "Mark available"}
              </button>
              <button data-testid={`svc-archive-${s.id}`} onClick={() => archive(s.id)} className="p-2" style={{ color: "var(--terracotta)" }}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
